import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { companyID } from "../auth";
import { roundToTwoDecimals } from "../utils/roundoffAmount";
import { Table } from "react-bootstrap";
import { convertDateFormate } from "../utils/convertDateFormate";
import { Spin } from "antd";
import { formatDecimal } from "../utils/formatDecimal";
import api from "../api/axios";
import { getDaysInMonth } from "../utils/getDaysInMonth";
import { getPerformanceBonusReleaseDate } from "../services/performanceBonusServices";

const SalaryDetail = ({
  employeeId,
  show,
  handleClose,
  handleGenerate,
  loading,
  setLoading,
  salaryId,
  salaryData,
  employeeName,
}) => {
  const [formData, setFormData] = useState({
    other_deduction: 0,
    net_salary: 0,
    totalPay: 0,
    total_deduction: 0,
    other_deduction_reason: "",
    other_earning_reason: "",
    released_performance_bonus: 0,
  });

  const [salaryComponents, setSalaryComponents] = useState([]);
  const [earningsComponents, setEarningsComponents] = useState([]);
  const [deductionsComponents, setDeductionsComponents] = useState([]);
  const [monthYear, setMonthYear] = useState("");
  const [companyWorkingDays, setCompanyWorkingDays] = useState(null);
  const [employeeWorkingDetail, setEmployeeWorkingDetail] = useState(null);
  const [currentSalary, setCurrentSalary] = useState(null);
  const [companySalaryStructure, setCompanySalaryStructure] = useState(null);
  const [companySalaryConfig, setCompanySalaryConfig] = useState(null);
  const [error, setError] = useState({});
  const [inEligibilityReason, setInEligibilityReason] = useState("");
  const [sendSalaryAcknowledgment, setSendSalaryAcknowledgment] = useState(true);

  const currentDate = new Date();

  const getMaxDate = () => {
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    let maxDate = `${currentYear}-${
      currentMonth < 10 ? "0" + currentMonth : currentMonth
    }`;
    return maxDate;
  };

  const company_id = companyID();

  const fetchSalaryComponents = async () => {
    try {
      const response = await api.get(`/api/salary/components`);
      if (response?.data?.success) {
        const components = response.data.data;
        setSalaryComponents(components);

        // Separate earnings and deductions
        const earnings = components.filter((comp) => comp.type === "EARNING");
        const deductions = components.filter((comp) => comp.type === "DEDUCTION");

        setEarningsComponents(earnings);
        setDeductionsComponents(deductions);
      }
    } catch (err) {
      console.error("Error fetching salary components:", err);
    }
  };

  const fetchCompanySalaryConfig = async () => {
    try {
      const response = await api.get(`salary-config/${company_id}`);
      setCompanySalaryConfig(response.data.config);
      return response.data.config;
    } catch (err) {
      console.error("Error fetching salary config:", err);
    }
  };

  useEffect(() => {
    if (company_id) {
      fetchCompanySalaryConfig();
      fetchSalaryComponents();
    }
  }, [company_id]);

  const calculateSalaryGenerationDate = React.useMemo(() => {
    if (!companySalaryConfig) return 0;
    const { payroll_release_date, payroll_processing_days } =
      companySalaryConfig;
    return payroll_release_date - payroll_processing_days;
  }, [companySalaryConfig]);

  const checkCorrectionPending = async (
    e,
    year,
    month,
    maxDate,
    changeDate
  ) => {
    const selectedDate = e.target.value;
    // const paddedMonth = month < 10 ? `0${month}` : `${month}`;

    const isCorrectionPending =
      companySalaryConfig?.payroll_release_month !== "same_month"
        ? (selectedDate === `${year}-${month - 1}` &&
            currentDate.getDate() < calculateSalaryGenerationDate) ||
          selectedDate === maxDate
        : currentDate.getDate() <= calculateSalaryGenerationDate &&
          selectedDate === maxDate;

    changeDate(selectedDate);
    setError((prev) => ({ ...prev, correctionPending: isCorrectionPending }));
  };

  const handleMonthYearChange = (e) => {
    const changeDate = (date) => {
      setMonthYear(date);
      setFormData((prevData) => ({
        ...prevData,
        other_deduction: 0,
        total_deduction: 0,
        other_deduction_reason: "",
        other_earning_reason: "",
      }));
      setError({});
      const inputField = document.getElementById("other_deduction");
      if (inputField) {
        inputField.value = "";
      }
      const deductionReasonInputField =
        document.getElementById("deduction_reason");
      if (deductionReasonInputField) {
        deductionReasonInputField.value = "";
      }
    };
    const maxDate = getMaxDate();
    const { year, month } = convertDateFormate(maxDate);
    checkCorrectionPending(e, year, month, maxDate, changeDate);
  };

  const selectedMonthYear = convertDateFormate(monthYear);

  const fetchCompanyWorkingDays = () => {
    setLoading(true);
    api
      .get(
        `working-days/${company_id}/${selectedMonthYear.month}/${selectedMonthYear.year}?employee_id=${employeeId}`
      )
      .then((res) => {
        setCompanyWorkingDays(res.data.company_working_days);
        fetchEmployeementData(res.data.employee_working_days);
      })
      .catch((err) => {
        console.error(err);
        setInEligibilityReason(err.response?.data?.message ?? "");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchEmployeementData = (companyWorkingDays) => {
    api
      .get(`employees/companies/${company_id}/employees/${employeeId}/salary-breakdown`, {
        params: { month: monthYear },
      })
      .then((res) => {
        setCurrentSalary(res.data.employmentData.salary);
        setCompanySalaryStructure(res.data.config);
        fetchEmployeeWorkingDetail(
          res.data.employmentData,
          res.data.config,
          companyWorkingDays
        );
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchEmployeeWorkingDetail = (
    employmentData,
    companySalaryStructure,
    companyWorkingDays
  ) => {
    api
      .post(`calculate-salary`, {
        employee_id: employeeId,
        company_id: company_id,
        month: selectedMonthYear.month,
        year: selectedMonthYear.year,
      })
      .then((res) => {
        setInEligibilityReason("");
        const salaryDetail = res.data.salaryDetails[0];
        setEmployeeWorkingDetail(salaryDetail);

        // Hour-based employees are paid on hours logged x hourly rate; everyone
        // else keeps the attendance-driven per-day calculation. Either way the
        // result feeds the same salary components below.
        let roundedSalary;
        if (salaryDetail?.is_hourly) {
          roundedSalary = roundToTwoDecimals(salaryDetail.hourly_gross || 0);
        } else {
          const per_day_salary = employmentData.salary / getDaysInMonth(monthYear);
          const current_month_salary =
            ((salaryDetail?.salary_eligible_days +
              companyWorkingDays.weekOffDatesCount) - (salaryDetail?.total_sandwich_holidays ?? 0)) * per_day_salary;
          roundedSalary = roundToTwoDecimals(current_month_salary);
        }

        // Calculate performance bonus
        const performance_bonus_amount = roundToTwoDecimals(
          (employmentData.performance_bonus * employmentData.salary) / 100
        );

        // Use dynamic salary calculation
        const salaryBreakdown = calculateDynamicSalary(roundedSalary, salaryComponents);
        
        // Calculate total deductions including performance bonus and other deductions
        const calculatedTotalDeduction = roundToTwoDecimals(
          Number(performance_bonus_amount || 0) +
          Number(salaryBreakdown.totalDeductions || 0) +
          Number(employmentData.other_deduction || 0)
        );

        // Merge earnings and deductions into formData with system values using correct ID
        const calculatedFormData = {
          ...Object.keys(salaryBreakdown.earnings).reduce((acc, key) => {
            acc[key] = {
              name: salaryBreakdown.earnings[key].name,
              id: salaryBreakdown.earnings[key].id, // Use id instead of component_id
              slug: salaryBreakdown.earnings[key].slug,
              systemValue: salaryBreakdown.earnings[key].value,
              changeValue: null, // Set to null when nothing has changed
              finalValue: salaryBreakdown.earnings[key].value
            };
            return acc;
          }, {}),
          ...Object.keys(salaryBreakdown.deductions).reduce((acc, key) => {
            acc[key] = {
              name: salaryBreakdown.deductions[key].name,
              id: salaryBreakdown.deductions[key].id, // Use id instead of component_id
              slug: salaryBreakdown.deductions[key].slug,
              systemValue: salaryBreakdown.deductions[key].value,
              changeValue: null, // Set to null when nothing has changed
              finalValue: salaryBreakdown.deductions[key].value
            };
            return acc;
          }, {}),
          net_salary: roundToTwoDecimals(
            salaryBreakdown.totalEarnings -
            performance_bonus_amount -
            salaryBreakdown.totalDeductions -
            (employmentData?.other_deduction || 0) +
            Number(roundToTwoDecimals(employmentData.total_reimbursement || 0))
          ),
          total_deduction: calculatedTotalDeduction,
          performance_bonus: Number(performance_bonus_amount),
          // Hourly employees have no monthly `salary` on record, so the earned
          // gross for the period stands in as the base figure on the slip.
          employee_salary: salaryDetail?.is_hourly
            ? Number(roundedSalary)
            : Number(employmentData.salary),
          total_reimbursement: Number(roundToTwoDecimals(employmentData.total_reimbursement || 0)),
          totalPay: roundToTwoDecimals(
            salaryBreakdown.totalEarnings +
            Number(roundToTwoDecimals(employmentData.total_reimbursement || 0))
          ),
          other_deduction: employmentData.other_deduction || 0,
          other_earning: 0,
          other_deduction_reason: employmentData.other_deduction_reason || "",
          released_performance_bonus: 0,
          ...res.data.salaryDetails[0],
        };

        setFormData((prevData) => ({
          ...prevData,
          ...calculatedFormData,
        }));
      })
      .catch((err) => {
        setInEligibilityReason(err?.response?.data?.message ?? "");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!show) {
      setSendSalaryAcknowledgment(true);
    }
    if (salaryId) {
      setLoading(false);
      setFormData(salaryData);
      setError((prevData) => ({ ...prevData, correctionPending: false }));
      setMonthYear(salaryData.month);
    } else if (employeeId && monthYear) {
      fetchCompanyWorkingDays();
    } else {
      //changes
      const maxDate = getMaxDate();
      setMonthYear(maxDate);
      console.log(
        companySalaryConfig?.payroll_release_month === "same_month" &&
          currentDate.getDate() >= calculateSalaryGenerationDate
      );
      if (
        companySalaryConfig?.payroll_release_month === "same_month" &&
        currentDate.getDate() >= calculateSalaryGenerationDate
      ) {
        setError((prevData) => ({
          ...prevData,
          correctionPending: false,
        }));
      } else {
        setError((prevData) => ({
          ...prevData,
          correctionPending: true,
        }));
      }

      // setError({})
      setFormData({
        other_deduction: 0,
        provident_fund: 0,
        performance_bonus: 0,
        net_salary: 0,
        totalPay: 0,
        total_deduction: 0,
        other_deduction_reason: "",
        other_earning_reason: "",
      });
      const inputField = document.getElementById("other_deduction");
      if (inputField) {
        inputField.value = "";
      }
      const deductionReasonInputField =
        document.getElementById("deduction_reason");
      if (deductionReasonInputField) {
        deductionReasonInputField.value = "";
      }
      const earningReasonInputField =
        document.getElementById("earning_reason");
      if (earningReasonInputField) {
        earningReasonInputField.value = "";
      }
    }
  }, [show, employeeId, monthYear, companySalaryConfig]);

  const companyThemeColor = localStorage.getItem("companyColor");

  const handleGenerateButton = () => {
    // Prepare salary components data for backend
    const salaryComponentsData = {
      earnings: [],
      deductions: []
    };

    // Process earnings components (skip zero-only entries)
    earningsComponents.forEach(component => {
      const componentData = formData[component.id]; // Use id instead of component_id
      if (componentData) {
        const systemValue = componentData.systemValue ?? 0;
        const changeValue = componentData.changeValue ?? null;
        const finalValue = componentData.finalValue ?? changeValue ?? systemValue ?? 0;

        // Skip components where all values are zero/empty
        const hasValue = Number(finalValue) !== 0 || Number(changeValue) !== 0 || Number(systemValue) !== 0;
        if (!hasValue) return;

        salaryComponentsData.earnings.push({
          id: component.id, // Use id instead of component_id
          name: componentData.name,
          slug: componentData.slug,
          systemValue,
          changeValue,
          finalValue
        });
      }
    });

    // Process deductions components (skip zero-only entries)
    deductionsComponents.forEach(component => {
      const componentData = formData[component.id]; // Use id instead of component_id
      if (componentData) {
        const systemValue = componentData.systemValue ?? 0;
        const changeValue = componentData.changeValue ?? null;
        const finalValue = componentData.finalValue ?? changeValue ?? systemValue ?? 0;

        // Skip components where all values are zero/empty
        const hasValue = Number(finalValue) !== 0 || Number(changeValue) !== 0 || Number(systemValue) !== 0;
        if (!hasValue) return;

        salaryComponentsData.deductions.push({
          id: component.id, // Use id instead of component_id
          name: componentData.name,
          slug: componentData.slug,
          systemValue,
          changeValue,
          finalValue
        });
      }
    });

    // Add other earning to earnings array
    if (formData.other_earning) {
      salaryComponentsData.earnings.push({
        id: 'other_earning',
        name: 'Other Earning',
        slug: 'other_earning',
        systemValue: 0,
        changeValue: roundToTwoDecimals(formData.other_earning || 0),
        finalValue: roundToTwoDecimals(formData.other_earning || 0),
        reason: formData.other_earning_reason || null
      });
    }

    // Add other deduction to deductions array
    if (formData.other_deduction) {
      salaryComponentsData.deductions.push({
        id: 'other_deduction',
        name: 'Other Deduction',
        slug: 'other_deduction',
        systemValue: 0,
        changeValue: roundToTwoDecimals(formData.other_deduction || 0),
        finalValue: roundToTwoDecimals(formData.other_deduction || 0),
        reason: formData.other_deduction_reason || null
      });
    }

    // Add performance bonus to deductions array
    if (formData.performance_bonus) {
      salaryComponentsData.deductions.push({
        id: 'performance_bonus',
        name: 'Performance Bonus',
        slug: 'performance_bonus',
        systemValue: formData.performance_bonus || 0,
        changeValue: roundToTwoDecimals(formData.performance_bonus || 0),
        finalValue: roundToTwoDecimals(formData.performance_bonus || 0)
      });
    }

    // Add reimbursement allowance to earnings array
    if (formData.total_reimbursement) {
      salaryComponentsData.earnings.push({
        id: 'total_reimbursement',
        name: 'Reimbursement Allowance',
        slug: 'total_reimbursement',
        systemValue: formData.total_reimbursement || 0,
        changeValue: roundToTwoDecimals(formData.total_reimbursement || 0),
        finalValue: roundToTwoDecimals(formData.total_reimbursement || 0)
      });
    }

    // Add released performance bonus to earnings array
    if (formData.released_performance_bonus) {
      salaryComponentsData.earnings.push({
        id: 'released_performance_bonus',
        name: 'Performance Bonus (Released)',
        slug: 'released_performance_bonus',
        systemValue: formData.released_performance_bonus || 0,
        changeValue: roundToTwoDecimals(formData.released_performance_bonus || 0),
        finalValue: roundToTwoDecimals(formData.released_performance_bonus || 0)
      });
    }

    const salaryData = {
      ...formData,
      month: selectedMonthYear.month,
      employee_id: employeeId,
      employee_name: employeeName || "",
      company_id,
      total_working_days:
        getDaysInMonth(monthYear) - companyWorkingDays?.weekOffDatesCount,
      ...employeeWorkingDetail,
      currentMonthSalary: formData.totalPay,
      slipMonth:
        new Date(
          selectedMonthYear.year,
          selectedMonthYear.month - 1
        ).toLocaleString("default", { month: "short" }) +
        " " +
        selectedMonthYear.year,
      salaryMonth: monthYear,
      net_salary: formData.net_salary,
      other_deduction: roundToTwoDecimals(formData.other_deduction),
      other_earning: roundToTwoDecimals(formData.other_earning || 0),
      employee_salary: Number(formData.employee_salary),
      other_deduction_reason: Number(formData.other_deduction)
        ? formData.other_deduction_reason
        : null,
      other_earning_reason: Number(formData.other_earning)
        ? formData.other_earning_reason
        : null,
      reimbursement_allowance: formData.total_reimbursement || 0,
      released_performance_bonus: formData.released_performance_bonus ?? 0,
      bonus_release_id: formData.bonus_release_id || null,
      // Add the structured salary components data
      salaryComponents: salaryComponentsData,
      // Legacy fields for backward compatibility
      provident_fund: formData.provident_fund || 0,
      gratuity: formData.gratuity || 0,
      special_allowance: formData.specialAllowance || 0,
      mobile_internet_allowance: formData.mobileInternetAllowance || 0,
      medical_insurance: formData.medicalInsurance || 0,
    };

    console.log("Salary Data for Backend:", salaryData);
    if (handleGenerate) {
      handleGenerate(salaryData, sendSalaryAcknowledgment);
    }
  };

  const handleDisableGenerateButton = () => {
    return inEligibilityReason ||
      error.other_deduction ||
      error.other_earning_reason ||
      (formData.other_deduction
        ? !formData.other_deduction_reason?.trim()
        : error.other_deduction_reason) ||
      (formData.other_earning
        ? !formData.other_earning_reason?.trim()
        : error.other_earning_reason)
      ? true
      : false;
  };

  useEffect(() => {
    if (employeeId && monthYear) {
      getPerformanceBonusReleaseDate(monthYear, employeeId)
        .then((res) => {
          if (res?.releaseData?.length > 0) {
            setFormData((prevData) => ({
              ...prevData,
              bonus_release_id: res.releaseData[0].id,
              released_performance_bonus: roundToTwoDecimals(
                res.releaseData[0].released_amount
              ),
              net_salary: roundToTwoDecimals(
                prevData.net_salary + res.releaseData[0].released_amount
              ),
            }));
          } else {
            setFormData((prevData) => ({
              ...prevData,
              released_performance_bonus: 0,
              bonus_release_id: null,
              net_salary: roundToTwoDecimals(
                prevData.net_salary - prevData.released_performance_bonus
              ),
              totalPay: roundToTwoDecimals(
                prevData.totalPay - prevData.released_performance_bonus
              ),
            }));
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [monthYear, employeeId]);

  // Reusable dynamic salary calculation function
  const calculateDynamicSalary = (baseSalary, components) => {
    const calculatedComponents = {
      earnings: {},
      deductions: {},
      totalEarnings: 0,
      totalDeductions: 0,
    };

    // Separate components by type
    const earnings = components.filter(comp => comp.type === "EARNING");
    const deductions = components.filter(comp => comp.type === "DEDUCTION");

    // First pass: Calculate FIXED and CTC-based components
    earnings.forEach((component) => {
      let value = 0;
      
      if (component.calculation_type === "FIXED") {
        value = roundToTwoDecimals(parseFloat(component.value));
      } else if (component.calculation_type === "PERCENTAGE" && component.calculation_base === "CTC") {
        value = roundToTwoDecimals((baseSalary * (parseFloat(component.value) || 0)) / 100);
      }
      
      // Always create the component entry, even if value is 0
      calculatedComponents.earnings[component.id] = {
        name: component.name,
        id: component.id,
        slug: component.slug,
        value: value
      };
      calculatedComponents.totalEarnings += value;
    });

    // Second pass: Calculate COMPONENT-based earnings
    earnings.forEach((component) => {
      if (
        component.calculation_type === "PERCENTAGE" &&
        component.calculation_base === "COMPONENT" &&
        component.calculation_base_component_id
      ) {
        // Find the base component by component_id, not by id
        const baseComponent = Object.values(calculatedComponents.earnings).find(
          comp => comp.id === component.calculation_base_component_id || 
          components.find(c => c.component_id === component.calculation_base_component_id && c.id === comp.id)
        );
        
        // If not found in earnings, search by component_id in the original components array
        let baseComponentValue = 0;
        if (baseComponent) {
          baseComponentValue = baseComponent.value;
        } else {
          // Find the component by component_id and then get its calculated value
          const baseComp = components.find(c => c.component_id === component.calculation_base_component_id);
          if (baseComp && calculatedComponents.earnings[baseComp.id]) {
            baseComponentValue = calculatedComponents.earnings[baseComp.id].value;
          }
        }

        if (baseComponentValue > 0) {
          const value = roundToTwoDecimals(
            (baseComponentValue * (parseFloat(component.value) || 0)) / 100
          );
          // Update the existing component entry
          calculatedComponents.earnings[component.id] = {
            name: component.name,
            id: component.id,
            slug: component.slug,
            value: value
          };
          calculatedComponents.totalEarnings += value;
        }
      }
    });

    // Calculate total gross for GROSS-based deductions
    const totalGross = calculatedComponents.totalEarnings;

    // Calculate deductions - always create entries for all deduction components
    deductions.forEach((component) => {
      let value = 0;
      
      if (component.calculation_type === "FIXED") {
        value = roundToTwoDecimals(parseFloat(component.value));
      } else if (component.calculation_type === "PERCENTAGE") {
        if (component.calculation_base === "CTC") {
          value = roundToTwoDecimals((baseSalary * (parseFloat(component.value) || 0)) / 100);
        } else if (component.calculation_base === "GROSS") {
          value = roundToTwoDecimals((totalGross * (parseFloat(component.value) || 0)) / 100);
        } else if (
          component.calculation_base === "COMPONENT" &&
          component.calculation_base_component_id
        ) {
          // Find the base component by component_id in earnings
          const baseComp = components.find(c => c.component_id === component.calculation_base_component_id);
          if (baseComp && calculatedComponents.earnings[baseComp.id]) {
            const baseComponentValue = calculatedComponents.earnings[baseComp.id].value;
            if (baseComponentValue > 0) {
              value = roundToTwoDecimals(
                (baseComponentValue * (parseFloat(component.value) || 0)) / 100
              );
            }
          }
        }
      }
      
      // Always create the component entry, even if value is 0
      calculatedComponents.deductions[component.id] = {
        name: component.name,
        id: component.id,
        slug: component.slug,
        value: value
      };
      calculatedComponents.totalDeductions += value;
    });

    return calculatedComponents;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle component value changes (make salary editable)
    if (name.startsWith('component_')) {
      const componentId = name.replace('component_', '');
      const component = [...earningsComponents, ...deductionsComponents].find(c => c.id == componentId); // Use id instead of component_id
      
      if (component) {
        const newValue = value === '' ? 0 : roundToTwoDecimals(parseFloat(value) || 0);
        setFormData(prev => ({
          ...prev,
          [componentId]: {
            ...prev[componentId],
            name: prev[componentId]?.name || component.name,
            id: componentId, // Use id instead of component_id
            slug: prev[componentId]?.slug || component.slug,
            systemValue: prev[componentId]?.systemValue || 0,
            changeValue: newValue === (prev[componentId]?.systemValue || 0) ? null : newValue,
            finalValue: newValue
          }
        }));
      }
    } else if (name === 'other_deduction') {
      const newValue = value === '' ? 0 : roundToTwoDecimals(parseFloat(value) || 0);
      setFormData(prev => ({
        ...prev,
        [name]: newValue,
      }));
    } else if (name === 'other_earning') {
      const newValue = value === '' ? 0 : roundToTwoDecimals(parseFloat(value) || 0);
      setFormData(prev => ({
        ...prev,
        [name]: newValue,
      }));
    } else if (name === 'performance_bonus') {
      const newValue = value === '' ? 0 : roundToTwoDecimals(parseFloat(value) || 0);
      setFormData(prev => ({
        ...prev,
        performance_bonus: newValue,
      }));
    } else if (name === 'total_reimbursement') {
      const newValue = value === '' ? 0 : roundToTwoDecimals(parseFloat(value) || 0);
      setFormData(prev => ({
        ...prev,
        total_reimbursement: newValue,
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDeductionReason = (e) => {
    const { name, value } = e.target;
    if (!value.trim()) {
      setError((prevData) => ({
        ...prevData,
        [name]: "Please mention deduction reason.",
      }));
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setError((prevData) => ({ ...prevData, [name]: null }));
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEarningReason = (e) => {
    const { name, value } = e.target;
    if (!value.trim()) {
      setError((prevData) => ({
        ...prevData,
        [name]: "Please mention earning reason.",
      }));
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setError((prevData) => ({ ...prevData, [name]: null }));
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Add useEffect to trigger recalculation when formData changes
  useEffect(() => {
    if (earningsComponents.length > 0 && deductionsComponents.length > 0) {
      recalculateSalary();
    }
  }, [formData.other_earning, formData.other_deduction]);

  // Separate useEffect for component changes
  useEffect(() => {
    if (earningsComponents.length > 0 && deductionsComponents.length > 0) {
      const hasComponentData = [...earningsComponents, ...deductionsComponents].some(comp => 
        formData[comp.id]?.finalValue !== undefined // Use id instead of component_id
      );
      if (hasComponentData) {
        recalculateSalary();
      }
    }
  }, [earningsComponents, deductionsComponents, formData]);

  const recalculateSalary = () => {
    let totalEarnings = 0;
    let totalDeductions = 0;

    // Calculate earnings from dynamic components using finalValue
    earningsComponents.forEach(component => {
      const componentData = formData[component.id]; // Use id instead of component_id
      if (componentData && typeof componentData.finalValue === 'number') {
        totalEarnings += componentData.finalValue;
      }
    });

    // Calculate deductions from dynamic components using finalValue
    deductionsComponents.forEach(component => {
      const componentData = formData[component.id]; // Use id instead of component_id
      if (componentData && typeof componentData.finalValue === 'number') {
        totalDeductions += componentData.finalValue;
      }
    });

    // Add other amounts
    const otherEarning = parseFloat(formData.other_earning) || 0;
    const otherDeduction = parseFloat(formData.other_deduction) || 0;
    const performanceBonus = parseFloat(formData.performance_bonus) || 0;
    const totalReimbursement = parseFloat(formData.total_reimbursement) || 0;
    const releasedPerformanceBonus = parseFloat(formData.released_performance_bonus) || 0;

    const calculatedTotalPay = roundToTwoDecimals(
      totalEarnings + 
      totalReimbursement + 
      releasedPerformanceBonus + 
      otherEarning
    );

    const calculatedTotalDeduction = roundToTwoDecimals(
      totalDeductions + 
      performanceBonus + 
      otherDeduction
    );

    const calculatedNetSalary = roundToTwoDecimals(calculatedTotalPay - calculatedTotalDeduction);
    
    // Only update if values have actually changed to prevent infinite loops
    setFormData(prev => {
      if (
        prev.totalPay !== calculatedTotalPay ||
        prev.total_deduction !== calculatedTotalDeduction ||
        prev.net_salary !== calculatedNetSalary
      ) {
        return {
          ...prev,
          totalPay: calculatedTotalPay,
          total_deduction: calculatedTotalDeduction,
          net_salary: calculatedNetSalary
        };
      }
      return prev;
    });
  };

  // Create dynamic rows with editable fields
  const createTableRows = () => {
    const rows = [];

    // Build local arrays so we can inject performance bonus as a deduction row to keep alignment
    const earningRows = [...earningsComponents];
    const deductionRows = [...deductionsComponents];

    // Inject performance bonus as a synthetic deduction row to avoid blank gap
    if (formData.performance_bonus > 0) {
      deductionRows.push({
        id: "__perf_bonus",
        name: "Performance Bonus",
        placeholder: true,
      });
    }

    const maxRows = Math.max(earningRows.length, deductionRows.length);

    for (let i = 0; i < maxRows; i++) {
      const earning = earningRows[i];
      const deduction = deductionRows[i];

      rows.push(
        <tr key={`row-${i}`}>
          <td>{earning?.name || ""}</td>
          <td>
            {earning ? (
              formData[earning.id] ? (
                <input
                  type="number"
                  className="form-control form-control-sm"
                  name={`component_${earning.id}`}
                  value={
                    formData[earning.id]?.changeValue !== null 
                      ? (formData[earning.id]?.changeValue === 0 ? '' : formData[earning.id]?.changeValue)
                      : (formData[earning.id]?.finalValue === 0 ? '' : formData[earning.id]?.finalValue || '')
                  }
                  onChange={handleInputChange}
                  onBlur={() => recalculateSalary()}
                  step="0.01"
                  min="0"
                  placeholder="0"
                />
              ) : (
                <span>0</span>
              )
            ) : null}
          </td>
          <td>{deduction?.name || ""}</td>
          <td>
            {deduction ? (
              deduction.id === "__perf_bonus" ? (
                <input
                  type="number"
                  className="form-control form-control-sm"
                  name="performance_bonus"
                  value={formData.performance_bonus === 0 ? '' : formData.performance_bonus || ''}
                  onChange={handleInputChange}
                  onBlur={() => recalculateSalary()}
                  step="0.01"
                  min="0"
                  placeholder="0"
                />
              ) : formData[deduction.id] ? (
                <input
                  type="number"
                  className="form-control form-control-sm"
                  name={`component_${deduction.id}`}
                  value={
                    formData[deduction.id]?.changeValue !== null 
                      ? (formData[deduction.id]?.changeValue === 0 ? '' : formData[deduction.id]?.changeValue)
                      : (formData[deduction.id]?.finalValue === 0 ? '' : formData[deduction.id]?.finalValue || '')
                  }
                  onChange={handleInputChange}
                  onBlur={() => recalculateSalary()}
                  step="0.01"
                  min="0"
                  placeholder="0"
                />
              ) : (
                <span>0</span>
              )
            ) : null}
          </td>
        </tr>
      );
    }

    // Performance bonus (released) on earnings side
    if (formData.released_performance_bonus > 0) {
      rows.push(
        <tr key="released-bonus">
          <td>Performance Bonus (Released)</td>
          <td>
            <strong>{formData.released_performance_bonus ? formatDecimal(formData.released_performance_bonus) : "-"}</strong>
          </td>
          <td></td>
          <td></td>
        </tr>
      );
    }

    // Reimbursement allowance shown only when greater than zero
    if (Number(formData.total_reimbursement) > 0) {
      rows.push(
        <tr key="reimbursement">
          <td>Reimbursement Allowance</td>
          <td>
            <input
              type="number"
              className="form-control form-control-sm"
              name="total_reimbursement"
              value={formData.total_reimbursement === 0 ? '' : formData.total_reimbursement || ''}
              onChange={handleInputChange}
              onBlur={() => recalculateSalary()}
              step="0.01"
              min="0"
              placeholder="0"
            />
          </td>
          <td></td>
          <td></td>
        </tr>
      );
    }

    // Add Other Earning and Other Deduction rows
    rows.push(
      <tr key="other-earning-deduction">
        <td>Other Earning</td>
        <td>
          <input
            type="number"
            className="form-control form-control-sm"
            name="other_earning"
            value={formData.other_earning === 0 ? '' : formData.other_earning || ''}
            onChange={handleInputChange}
            onBlur={() => recalculateSalary()}
            step="0.01"
            min="0"
            placeholder="0"
          />
        </td>
        <td>Other Deduction</td>
        <td>
          <input
            type="number"
            className="form-control form-control-sm"
            name="other_deduction"
            value={formData.other_deduction === 0 ? '' : formData.other_deduction || ''}
            onChange={handleInputChange}
            onBlur={() => recalculateSalary()}
            step="0.01"
            min="0"
            placeholder="0"
          />
        </td>
      </tr>
    );



    // Add the final row with totals
    rows.push(
      <tr key="totals-row" style={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
        <td>Gross Salary</td>
        <td>
          <strong>{formData.totalPay ? formatDecimal(formData.totalPay) : "-"}</strong>
        </td>
        <td>Total Deduction</td>
        <td>
          <strong>{formData.total_deduction ? formatDecimal(formData.total_deduction) : "-"}</strong>
        </td>
      </tr>
    );

    return rows;
  };

  return (
    <Modal show={show} centered size="lg">
      <Modal.Header className="d-flex justify-content-between ">
        <Modal.Title>Salary Details of {monthYear}</Modal.Title>
        {salaryId ? (
          <></>
        ) : (
          <Form.Group className="mb-0 d-flex justify-content-between">
            <Form.Control
              style={{ cursor: "pointer" }}
              id="monthInput"
              type="month"
              value={monthYear}
              onChange={handleMonthYearChange}
              max={getMaxDate()}
            />
          </Form.Group>
        )}
      </Modal.Header>
      {loading ? (
        <Spin
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "10px",
          }}
          size="large"
        />
      ) : error?.correctionPending ? (
        <Modal.Body>
          <p className="text-danger">
            {`* You have to wait until the ${
              companySalaryConfig?.payroll_release_month !== "same_month"
                ? new Date(
                    selectedMonthYear.year,
                    selectedMonthYear.month
                  ).toLocaleString("default", { month: "long" })
                : new Date(
                    selectedMonthYear.year,
                    selectedMonthYear.month - 1
                  ).toLocaleString("default", { month: "long" })
            } ${calculateSalaryGenerationDate}th for salary of ${new Date(
              selectedMonthYear.year,
              selectedMonthYear.month - 1
            ).toLocaleString("default", { month: "long" })}.`}
          </p>
        </Modal.Body>
      ) : (
        <Modal.Body>
          {salaryId || inEligibilityReason ? (
            <></>
          ) : employeeWorkingDetail?.is_hourly ? (
            <div
              className="d-flex gap-2 justify-content-between"
              style={{ color: "black" }}
            >
              <p>
                Hours worked:{" "}
                {formatDecimal(employeeWorkingDetail?.total_hours)} Hrs
              </p>
              <p>
                Rate per hour:{" "}
                {formatDecimal(employeeWorkingDetail?.hourly_rate)}
              </p>
              <p>
                Gross: {formatDecimal(employeeWorkingDetail?.hourly_gross)}
              </p>
            </div>
          ) : (
            <div>
              <div
                className="d-flex  gap-2 justify-content-between"
                style={{ color: "black" }}
              >
                <p>
                  Company holidays: {employeeWorkingDetail?.total_holidays} Day
                </p>
                <p>
                  Leaves: {formatDecimal(employeeWorkingDetail?.total_leave)}{" "}
                  Day
                </p>
                <p>
                  Attendance:{" "}
                  {formatDecimal(employeeWorkingDetail?.total_attendance)} Day
                </p>
              </div>
              <div
                className="d-flex gap-2 justify-content-between"
                style={{ color: "black" }}
              >
                <p>
                  Employee worked:{" "}
                  {formatDecimal(employeeWorkingDetail?.salary_eligible_days)}{" "}
                  Day
                </p>
                <p>
                  Company working:{" "}
                  {monthYear &&
                    getDaysInMonth(monthYear) -
                      companyWorkingDays?.weekOffDatesCount}{" "}
                  Day
                </p>
              </div>
            </div>
          )}
          {(Number(employeeWorkingDetail?.total_attendance) ||
            Number(employeeWorkingDetail?.total_leave) ||
            Number(employeeWorkingDetail?.total_hours) ||
            salaryId) &&
          !inEligibilityReason ? (
            <Form>
              <Row>
                <Table bordered responsive>
                  <thead>
                    <tr style={{ textAlign: "center" }}>
                      <th
                        colSpan={2}
                        style={{
                          backgroundColor: companyThemeColor,
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        Earnings
                      </th>
                      <th
                        colSpan={2}
                        style={{
                          backgroundColor: companyThemeColor,
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        Deductions
                      </th>
                    </tr>
                  </thead>
                  <tbody>{createTableRows()}</tbody>
                  <tfoot>
                    <tr>
                      <th
                        colSpan={2}
                        style={{
                          backgroundColor: companyThemeColor,
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        Net Salary
                      </th>
                      <th
                        colSpan={2}
                        style={{
                          backgroundColor: companyThemeColor,
                          color: "white",
                          textAlign: "end",
                        }}
                      >
                        {formData?.net_salary
                          ? formatDecimal(formData?.net_salary)
                          : "-"}
                      </th>
                    </tr>
                  </tfoot>
                </Table>
              </Row>
            </Form>
          ) : (
            <p className="text-danger">
              *{" "}
              {inEligibilityReason
                ? inEligibilityReason
                : `Not eligible for 
              ${new Date(
                selectedMonthYear.year,
                selectedMonthYear.month - 1
              ).toLocaleString("default", { month: "long" })}
              salary.`}
            </p>
          )}
          
          {/* Reason fields at the bottom */}
          {((formData.other_deduction > 0) || (formData.other_earning > 0)) && (
            <div className="mt-3">
              <Row>
                {formData.other_deduction > 0 && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Deduction Reason</Form.Label>
                      <Form.Control
                        as="textarea"
                        id="deduction_reason"
                        name="other_deduction_reason"
                        value={formData.other_deduction_reason || ""}
                        onChange={handleDeductionReason}
                        placeholder="Enter deduction reason"
                        rows={2}
                        isInvalid={!!error.other_deduction_reason}
                      />
                      {error.other_deduction_reason && (
                        <Form.Control.Feedback type="invalid">
                          {error.other_deduction_reason}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                )}
                {formData.other_earning > 0 && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Other Earning Reason</Form.Label>
                      <Form.Control
                        as="textarea"
                        id="earning_reason"
                        name="other_earning_reason"
                        value={formData.other_earning_reason || ""}
                        onChange={handleEarningReason}
                        placeholder="Enter earning reason"
                        rows={2}
                        isInvalid={!!error.other_earning_reason}
                      />
                      {error.other_earning_reason && (
                        <Form.Control.Feedback type="invalid">
                          {error.other_earning_reason}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </Modal.Body>
      )}
      <Modal.Footer>
        {!salaryId && (
          <Form.Check
            type="checkbox"
            id="salary-acknowledgment"
            className="me-auto"
            label="Send salary acknowledgment"
            checked={sendSalaryAcknowledgment}
            onChange={(e) => setSendSalaryAcknowledgment(e.target.checked)}
          />
        )}
        <Button
          variant="danger"
          onClick={() => {
            setFormData({
              other_deduction: 0,
              provident_fund: 0,
              performance_bonus: 0,
              net_salary: 0,
              totalPay: 0,
              total_deduction: 0,
              other_deduction_reason: "",
              other_earning_reason: "",
            });
            setMonthYear("");
            setSendSalaryAcknowledgment(true);
            handleClose();
          }}
        >
          Cancel
        </Button>
        {!error?.correctionPending &&
        !inEligibilityReason &&
        (Number(employeeWorkingDetail?.total_attendance) ||
          Number(employeeWorkingDetail?.total_leave) ||
          Number(employeeWorkingDetail?.total_hours)) ? (
          <Button
            variant="primary"
            disabled={handleDisableGenerateButton()}
            onClick={handleGenerateButton}
          >
            Generate
          </Button>
        ) : (
          <></>
        )}
        {salaryId ? (
          <Button
            variant="primary"
            disabled={handleDisableGenerateButton()}
            onClick={() => {}}
          >
            Update
          </Button>
        ) : (
          <></>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SalaryDetail;
